import { Suspense, lazy, useRef, useState, type ChangeEvent } from "react";
import { Code2, Download, Loader2, Upload, X } from "lucide-react";
import { useStudioStore } from "@/store/studio-store";
import { OPEN_CODE_CREATOR_NOTICE } from "@/features/validation-bridge/refinement-handlers";
import {
  exportProjectBundle,
  importProjectBundle,
} from "@/services/project-api";
import { toast } from "@/store/toast-store";
import { AskCogoSurfaceButton } from "../components/ask-cogo-surface-button";
import { useStudioLocale } from "@/i18n";
import { getRailSurfaceCopy } from "./rail-surface-copy";

const JsonEditorPanel = lazy(() =>
  import("@/features/json-editor/components/json-editor-panel").then((m) => ({
    default: m.JsonEditorPanel,
  })),
);

/**
 * persona-ux-10 — persistent code-surface notice for creators.
 *
 * A creator reaches the code surface via an Open Code next-action WITHOUT
 * persona escalation (F31, the fix for the silent permanent promotion). But the
 * 'code' rail icon stays hidden for creators, so the only breadcrumb used to be
 * a transient ~2.5s info toast — a creator who paused lost it and saw a code
 * view with no matching rail entry. This in-surface banner is the persistent
 * affordance: it reuses the toast's copy (OPEN_CODE_CREATOR_NOTICE) and offers a
 * one-click switch to 'All tools' (developer persona) so the 'code' rail icon
 * and its tools stay open. Dismissible (session-local) for creators who just
 * want the one-time view. Developers never see it (they already have the icon).
 */
function CreatorCodeNotice() {
  const { locale } = useStudioLocale();
  const copy = getRailSurfaceCopy(locale).code;
  const isCreator = useStudioStore((s) => s.studioPersona !== "developer");
  const setStudioPersona = useStudioStore((s) => s.setStudioPersona);
  const [dismissed, setDismissed] = useState(false);

  if (!isCreator || dismissed) return null;

  return (
    <div
      role="note"
      data-testid="creator-code-notice"
      className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
    >
      <Code2 className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} aria-hidden />
      {/* The notice sentence itself is owned by features/validation-bridge
          (OPEN_CODE_CREATOR_NOTICE, shared with the Open-Code toast) — the
          slot is marked so this surface's i18n gate reports it as that lane's
          string rather than silently covering for it. */}
      <span className="min-w-0 flex-1" data-i18n-owner="features/validation-bridge">
        {OPEN_CODE_CREATOR_NOTICE}
      </span>
      <button
        type="button"
        onClick={() => setStudioPersona("developer")}
        className="shrink-0 rounded border border-border bg-card px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-accent transition-colors"
      >
        {copy.enableAllTools}
      </button>
      <button
        type="button"
        aria-label={copy.dismiss}
        onClick={() => setDismissed(true)}
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}

function CodeProjectBundleToolbar() {
  const { locale } = useStudioLocale();
  const copy = getRailSurfaceCopy(locale).code;
  const currentProjectId = useStudioStore((s) => s.currentProjectId);
  const openServerProject = useStudioStore((s) => s.openServerProject);
  const setStudioMode = useStudioStore((s) => s.setStudioMode);
  const setStudioPersona = useStudioStore((s) => s.setStudioPersona);
  const setViewMode = useStudioStore((s) => s.setViewMode);
  const setActiveRailItem = useStudioStore((s) => s.setActiveRailItem);
  const setInspectorPanelOpen = useStudioStore((s) => s.setInspectorPanelOpen);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState<"export" | "import" | null>(null);

  const disabled = !currentProjectId || Boolean(busy);

  const handleExport = async () => {
    if (!currentProjectId || busy) return;
    setBusy("export");
    try {
      const bundle = await exportProjectBundle(currentProjectId);
      const json = JSON.stringify(bundle, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeDownloadName(bundle.project.name)}.cogo-studio.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(copy.exported);
    } catch (error) {
      console.error("[CodeSurface] export failed:", error);
      toast.error(
        copy.exportFailed(
          error instanceof Error ? error.message : copy.unknownError,
        ),
      );
    } finally {
      setBusy(null);
    }
  };

  const handleImportClick = () => {
    if (busy) return;
    inputRef.current?.click();
  };

  const handleImportFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || busy) return;

    setBusy("import");
    try {
      const parsed = JSON.parse(await file.text()) as unknown;
      const result = await importProjectBundle(parsed);
      await openServerProject(result.projectId);
      setStudioMode("edit");
      setStudioPersona("developer");
      setViewMode("editor");
      setActiveRailItem("code");
      setInspectorPanelOpen(false);
      toast.success(copy.imported(result.name));
    } catch (error) {
      console.error("[CodeSurface] import failed:", error);
      toast.error(
        copy.importFailed(
          error instanceof Error ? error.message : copy.unknownError,
        ),
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="code-project-bundle-toolbar"
      className="flex min-h-10 flex-wrap items-center justify-between gap-2 border-b border-border bg-background px-3 py-1.5"
    >
      <div className="min-w-0 max-sm:w-full">
        <div className="text-xs font-medium text-foreground">
          {copy.bundleTitle}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {copy.bundleDescription}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1.5 max-sm:w-full">
        <AskCogoSurfaceButton
          label={copy.reviewBundle}
          context={() =>
            [
              "Studio code surface: review raw Coogo JSON, project bundle import/export, and developer-mode changes.",
              currentProjectId ? `Project=${currentProjectId}` : "Project=none",
            ].join(" ")
          }
        />
        <button
          type="button"
          data-testid="code-export-cogo-bundle"
          aria-label={copy.exportBundle}
          title={copy.exportBundle}
          disabled={disabled}
          onClick={() => {
            void handleExport();
          }}
          className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-card px-2 text-[11px] font-medium text-foreground transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-40"
        >
          {busy === "export" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          {copy.exportAction}
        </button>
        <button
          type="button"
          data-testid="code-import-cogo-bundle"
          aria-label={copy.importBundle}
          title={copy.importBundle}
          disabled={Boolean(busy)}
          onClick={handleImportClick}
          className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-card px-2 text-[11px] font-medium text-foreground transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-40"
        >
          {busy === "import" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          {copy.importAction}
        </button>
        <input
          ref={inputRef}
          data-testid="code-import-cogo-bundle-input"
          className="hidden"
          type="file"
          accept=".json,.cogo.json,.cogo-studio.json,application/json"
          onChange={handleImportFile}
        />
      </div>
    </div>
  );
}

function safeDownloadName(name: string): string {
  const safe = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return safe || "cogo-project";
}

/**
 * Code surface — IDE v2 inner shell for the Code rail item.
 *
 * The Code surface now follows the same Studio light/dark theme contract as
 * the rest of the shell. `.ide-code-shell` keeps code-specific tokens scoped,
 * while JsonEditorPanel and vanilla-jsoneditor resolve theme from the global
 * Studio theme instead of being forced dark.
 */
export function CodeSurface() {
  return (
    <div
      data-testid="code-workspace-surface"
      className="ide-code-shell flex h-full flex-col"
    >
      <CreatorCodeNotice />
      <CodeProjectBundleToolbar />
      <div className="min-h-0 flex-1">
        <Suspense fallback={<div className="h-full" />}>
          <JsonEditorPanel />
        </Suspense>
      </div>
    </div>
  );
}
